import * as THREE from 'three';

/**
 * Handles camera movement, animation, and controls
 */
export class CameraController {
  constructor(camera, clock, scene = null) {
    this.camera = camera;
    this.clock = clock;
    this.scene = scene;
    this.cameraAnimation = null;

    // Mouse/touch interaction state
    this.isDragging = false;
    this.pointerDownPosition = { x: 0, y: 0 };
    this.previousPointerPosition = { x: 0, y: 0 };
    this.euler = new THREE.Euler(0, 0, 0, 'YXZ');
    this.pointerDownTime = 0;

    // Multitouch zoom state
    this.touches = new Map();
    this.initialPinchDistance = 0;
    this.isZooming = false;
    this.baseFov = this.camera.fov; // Store original field of view
    this.minFov = 10;  // More zoomed in (narrower field of view)
    this.maxFov = 120; // More zoomed out (wider field of view)


    // Performance optimization - pre-allocated objects
    this.tempVector = new THREE.Vector3();
    this.touchArray = [null, null];
    this.lastZoomUpdate = 0;

    // WASD movement controls
    this.keysPressed = {};
    this.movementSpeed = 0.035; // Movement speed

    // Virtual joystick for mobile
    this.joystickActive = false;
    this.joystickVector = { x: 0, y: 0 };
    this.joystickStart = { x: 0, y: 0 };
    this.joystickTouchId = null;

    // Current room container for collision detection
    this.currentRoomContainer = null;
  }

  /**
   * Set the current room container for collision detection
   */
  setCurrentRoomContainer(container) {
    this.currentRoomContainer = container;
  }

  /**
   * Check if user is interacting with UI elements (forms, menus, etc.)
   */
  isUIFocused() {
    const activeElement = document.activeElement;
    const tagName = activeElement?.tagName?.toLowerCase();

    // Check if focus is on an input element
    if (['input', 'textarea', 'select'].includes(tagName)) {
      return true;
    }

    // Check if focus is on a contenteditable element (e.g., rich text editors, chat boxes)
    if (activeElement?.isContentEditable) {
      return true;
    }

    // Check if any modal/menu is VISIBLE (not just present in DOM)
    const checkVisible = (selector) => {
      const element = document.querySelector(selector);
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    };

    const hasOpenModal = checkVisible('[role="dialog"]') ||
                        checkVisible('.modal') ||
                        checkVisible('[class*="popup"][style*="display"]') ||
                        checkVisible('[class*="overlay"][style*="display"]');

    return hasOpenModal;
  }

  /**
   * Handle keyboard down for WASD movement
   */
  onKeyDown(event) {
    // Don't process movement keys if user is typing in an input field or menu is open
    if (this.isUIFocused()) {
      return;
    }

    // Use event.code for physical key position (works with all keyboard layouts)
    const code = event.code;
    const validCodes = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

    if (validCodes.includes(code)) {
      this.keysPressed[code] = true;
      event.preventDefault();
    }
  }

  /**
   * Handle keyboard up for WASD and Arrow keys movement
   */
  onKeyUp(event) {
    // Use event.code for physical key position (works with all keyboard layouts)
    const code = event.code;
    const validCodes = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

    if (validCodes.includes(code)) {
      this.keysPressed[code] = false;
    }
  }

  /**
   * Handle touch for virtual joystick (bottom-left area only)
   */
  handleJoystickTouch(event) {
    const touch = event.touches[0];
    // Check if touch is in bottom-left corner (joystick area)
    if (touch.clientX < 150 && touch.clientY > window.innerHeight - 150) {
      this.joystickTouchId = touch.identifier;
      this.joystickStart = { x: touch.clientX, y: touch.clientY };
      this.joystickActive = true;
      return true; // Handled by joystick
    }
    return false; // Not joystick area
  }

  updateJoystickVector(event) {
    if (this.joystickTouchId === null) return;

    const touch = Array.from(event.touches).find(t => t.identifier === this.joystickTouchId);
    if (!touch) return;

    const deltaX = touch.clientX - this.joystickStart.x;
    const deltaY = touch.clientY - this.joystickStart.y;
    const maxDistance = 50;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const clampedDistance = Math.min(distance, maxDistance);

    if (distance > 0) {
      this.joystickVector = {
        x: (deltaX / distance) * (clampedDistance / maxDistance),
        y: (deltaY / distance) * (clampedDistance / maxDistance)
      };
    }
  }

  endJoystickTouch(event) {
    const touches = Array.from(event.changedTouches);
    if (touches.some(t => t.identifier === this.joystickTouchId)) {
      this.joystickTouchId = null;
      this.joystickActive = false;
      this.joystickVector = { x: 0, y: 0 };
      return true;
    }
    return false;
  }

  /**
   * Handle pointer down events
   */
  onPointerDown(event) {
    // IGNORE touches in joystick area (bottom-center)
    const centerX = window.innerWidth / 2;
    if (Math.abs(event.clientX - centerX) < 100 && event.clientY > window.innerHeight - 200) {
      return; // Let joystick handle this
    }

    // Store touch for multitouch tracking
    this.touches.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      startX: event.clientX,
      startY: event.clientY
    });

    // Check for zoom start (two touches)
    if (this.touches.size === 2) {
      this.isZooming = true;
      this.isDragging = false;

      // Reuse pre-allocated array
      let i = 0;
      for (const touch of this.touches.values()) {
        this.touchArray[i++] = touch;
      }

      this.initialPinchDistance = Math.hypot(
        this.touchArray[1].x - this.touchArray[0].x,
        this.touchArray[1].y - this.touchArray[0].y
      );

      // Store base field of view for zoom
      this.baseFov = this.camera.fov;
      this.lastZoomUpdate = Date.now();
      return;
    }

    // Single touch - normal look controls
    if (this.touches.size === 1) {
      this.isDragging = false;
      this.isZooming = false;
      this.pointerDownTime = Date.now();
      this.pointerDownPosition.x = event.clientX;
      this.pointerDownPosition.y = event.clientY;
      this.previousPointerPosition.x = event.clientX;
      this.previousPointerPosition.y = event.clientY;
    }
  }

  /**
   * Handle pointer move events for camera look controls and zoom
   */
  onPointerMove(event) {
    // IGNORE touches in joystick area (bottom-center)
    const centerX = window.innerWidth / 2;
    if (Math.abs(event.clientX - centerX) < 100 && event.clientY > window.innerHeight - 200) {
      return; // Let joystick handle this
    }

    // Update touch position if this pointer exists
    if (this.touches.has(event.pointerId)) {
      this.touches.set(event.pointerId, {
        ...this.touches.get(event.pointerId),
        x: event.clientX,
        y: event.clientY
      });
    }

    // Handle zoom (two touches) - throttled for performance
    if (this.touches.size === 2 && this.isZooming) {
      const now = Date.now();
      if (now - this.lastZoomUpdate < 16) { // ~60fps limit
        return;
      }

      // Reuse pre-allocated array
      let i = 0;
      for (const touch of this.touches.values()) {
        this.touchArray[i++] = touch;
      }

      const currentDistance = Math.hypot(
        this.touchArray[1].x - this.touchArray[0].x,
        this.touchArray[1].y - this.touchArray[0].y
      );

      if (this.initialPinchDistance > 0) {
        const zoomFactor = currentDistance / this.initialPinchDistance;

        // Calculate new field of view (inverse relationship: smaller FOV = more zoomed in)
        const fovChange = (1 - zoomFactor) * 30;
        let newFov = this.baseFov + fovChange;

        // Simple FOV limits
        newFov = Math.max(this.minFov, Math.min(this.maxFov, newFov));

        this.camera.fov = newFov;
        this.camera.updateProjectionMatrix();

        this.baseFov = newFov;
        this.initialPinchDistance = currentDistance;
      }

      this.lastZoomUpdate = now;
      return;
    }

    // Handle single touch look controls
    if (this.touches.size === 1 && !this.isZooming) {
      if (this.pointerDownPosition.x === 0 && this.pointerDownPosition.y === 0) return;

      const isTouch = event.pointerType === 'touch' || 'ontouchstart' in window;
      const isMouse = event.pointerType === 'mouse';

      if (isMouse && event.buttons === 0) {
        this.isDragging = false;
        this.pointerDownPosition.x = 0;
        this.pointerDownPosition.y = 0;
        return;
      }

      const deltaX = event.clientX - this.previousPointerPosition.x;
      const deltaY = event.clientY - this.previousPointerPosition.y;

      const movementThreshold = isTouch ? 3 : 1;
      if (Math.abs(deltaX) > movementThreshold || Math.abs(deltaY) > movementThreshold) {
        this.isDragging = true;

        // Adjust sensitivity based on zoom level (FOV)
        const zoomFactor = this.camera.fov / 75; // 75 is default FOV
        const baseSensitivity = isTouch ? 0.004 : 0.002;
        const sensitivity = baseSensitivity * zoomFactor;

        this.euler.setFromQuaternion(this.camera.quaternion);
        this.euler.y += deltaX * sensitivity;
        this.euler.x += deltaY * sensitivity;
        this.euler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.euler.x));

        this.camera.quaternion.setFromEuler(this.euler);
      }

      this.previousPointerPosition.x = event.clientX;
      this.previousPointerPosition.y = event.clientY;
    }
  }

  /**
   * Handle pointer up events - returns true if it was a tap/click (not drag)
   */
  onPointerUp(event) {
    // Remove touch from tracking
    const touch = this.touches.get(event.pointerId);
    this.touches.delete(event.pointerId);

    // If we were zooming and now have less than 2 touches, stop zooming
    if (this.isZooming && this.touches.size < 2) {
      this.isZooming = false;
    }

    // Only process tap detection for single touch that just ended
    if (!touch || this.isZooming || this.touches.size > 0) {
      return false;
    }

    const touchDuration = Date.now() - this.pointerDownTime;
    const dx = Math.abs(event.clientX - touch.startX);
    const dy = Math.abs(event.clientY - touch.startY);

    const isTouch = event.pointerType === 'touch' || 'ontouchstart' in window || event.type.includes('touch');
    const clickThreshold = isTouch ? 25 : 10;
    const tapTimeThreshold = isTouch ? 300 : 200;

    const isTap = touchDuration < tapTimeThreshold && dx < clickThreshold && dy < clickThreshold && !this.isDragging;

    // Reset state for single touch
    this.pointerDownPosition.x = 0;
    this.pointerDownPosition.y = 0;
    this.isDragging = false;
    this.pointerDownTime = 0;

    return isTap;
  }

  /**
   * Handle mouse wheel zoom
   */
  onWheel(event) {
    event.preventDefault();

    const zoomAmount = event.deltaY > 0 ? 5 : -5; // Zoom out/in
    const newFov = Math.max(this.minFov, Math.min(this.maxFov, this.camera.fov + zoomAmount));

    this.camera.fov = newFov;
    this.camera.updateProjectionMatrix();
    this.baseFov = newFov;
  }

  /**
   * Animate camera to target position and look at target
   */
  animateCamera(targetPosition, lookAtTarget, onComplete) {
    if (this.cameraAnimation) {
      this.cameraAnimation.progress = 1.0;
    }

    const startPosition = this.camera.position.clone();
    const startQuaternion = this.camera.quaternion.clone();

    let targetQuaternion;
    if (lookAtTarget) {
      const tempCamera = this.camera.clone();
      tempCamera.position.copy(targetPosition);
      tempCamera.lookAt(lookAtTarget);
      targetQuaternion = tempCamera.quaternion;
    } else {
      targetQuaternion = this.camera.quaternion.clone();
    }

    this.cameraAnimation = {
      startPosition,
      targetPosition,
      startQuaternion,
      targetQuaternion,
      progress: 0,
      duration: 0.7,
      onComplete
    };
  }

  /**
   * Update camera animation (called from main animation loop)
   */
  updateCameraAnimation(delta) {
    if (!this.cameraAnimation) return;

    this.cameraAnimation.progress += delta / this.cameraAnimation.duration;
    const easeProgress = 0.5 - 0.5 * Math.cos(this.cameraAnimation.progress * Math.PI);

    if (this.cameraAnimation.progress >= 1) {
      this.camera.position.copy(this.cameraAnimation.targetPosition);
      this.camera.quaternion.copy(this.cameraAnimation.targetQuaternion);
      if (this.cameraAnimation.onComplete) {
        this.cameraAnimation.onComplete();
      }
      this.cameraAnimation = null;
    } else {
      this.camera.position.lerpVectors(
        this.cameraAnimation.startPosition,
        this.cameraAnimation.targetPosition,
        easeProgress
      );
      this.camera.quaternion.copy(this.cameraAnimation.startQuaternion).slerp(
        this.cameraAnimation.targetQuaternion,
        easeProgress
      );
    }
  }

  /**
   * Update WASD movement (called from main animation loop)
   */
  updateMovement() {
    // Check if there's any movement input (keyboard or joystick)
    // Use event.code keys (KeyW, KeyA, etc.)
    const hasKeyboardInput = this.keysPressed['KeyW'] || this.keysPressed['KeyS'] ||
                             this.keysPressed['KeyA'] || this.keysPressed['KeyD'] ||
                             this.keysPressed['ArrowUp'] || this.keysPressed['ArrowDown'] ||
                             this.keysPressed['ArrowLeft'] || this.keysPressed['ArrowRight'];
    const hasJoystickInput = this.joystickActive;

    // No input = no movement = no collision check needed
    if (!hasKeyboardInput && !hasJoystickInput) {
      return;
    }

    const direction = new THREE.Vector3();

    // Calculate movement from keyboard (WASD and Arrow keys)
    // Using event.code for physical key positions
    if (this.keysPressed['KeyW'] || this.keysPressed['ArrowUp']) direction.z -= 1;
    if (this.keysPressed['KeyS'] || this.keysPressed['ArrowDown']) direction.z += 1;
    if (this.keysPressed['KeyA'] || this.keysPressed['ArrowLeft']) direction.x -= 1; // LEFT
    if (this.keysPressed['KeyD'] || this.keysPressed['ArrowRight']) direction.x += 1; // RIGHT

    // Add joystick movement and handle camera rotation for forward zone
    if (this.joystickActive) {
      direction.x += this.joystickVector.x;
      direction.z += this.joystickVector.y;

      // Calculate joystick angle (-180 to 180 degrees)
      // Screen coords: Y is positive DOWN, so forward (up on screen) = negative Y
      // 0° = right, -90° = up/forward, 90° = down/backward, ±180° = left
      const joystickAngle = Math.atan2(this.joystickVector.y, this.joystickVector.x) * (180 / Math.PI);

      // Forward zone: -160° to -20° (140° arc, where -90° is straight up/forward)
      // This means: pushing upward on the joystick (negative Y values)
      const isInForwardZone = joystickAngle >= -160 && joystickAngle <= -20;

      // DEBUG: Log joystick data
      if (Math.abs(this.joystickVector.x) > 0.05 || Math.abs(this.joystickVector.y) > 0.05) {
        console.log('Joystick:', {
          x: this.joystickVector.x.toFixed(2),
          y: this.joystickVector.y.toFixed(2),
          angle: joystickAngle.toFixed(1),
          inForwardZone: isInForwardZone
        });
      }

      if (isInForwardZone) {
        // Rotate camera to follow the movement direction
        // Use the joystick's X component to rotate camera left/right
        const rotationSpeed = this.joystickVector.x * 0.008; // Much slower rotation

        // Apply rotation to camera's Y axis (horizontal rotation)
        this.euler.setFromQuaternion(this.camera.quaternion);
        this.euler.y -= rotationSpeed; // Negative for natural left/right feel
        this.camera.quaternion.setFromEuler(this.euler);

        console.log('Rotating camera, speed:', rotationSpeed.toFixed(3));
      }
    }

    if (direction.length() > 0) {
      direction.normalize();

      // Store original Y position FIRST
      const originalY = this.camera.position.y;

      // Get camera's forward and right vectors (COMPLETELY locked to horizontal plane)
      const cameraDirection = new THREE.Vector3();
      this.camera.getWorldDirection(cameraDirection);
      cameraDirection.y = 0; // Zero out Y component
      cameraDirection.normalize();

      const cameraRight = new THREE.Vector3();
      cameraRight.set(-cameraDirection.z, 0, cameraDirection.x); // Perpendicular on XZ plane, Y=0
      cameraRight.normalize();

      // Calculate movement in world space (Y will be 0)
      const movement = new THREE.Vector3();
      movement.addScaledVector(cameraDirection, -direction.z * this.movementSpeed);
      movement.addScaledVector(cameraRight, direction.x * this.movementSpeed);
      movement.y = 0; // FORCE Y to 0

      // Calculate new position
      const newPosition = this.camera.position.clone();
      newPosition.x += movement.x;
      newPosition.z += movement.z;
      newPosition.y = originalY; // Keep Y exactly the same

      // Collision detection with current room only (only runs when moving)
      const roomObjects = this.currentRoomContainer ? [this.currentRoomContainer] : null;

      if (this.scene && this.checkCollision(newPosition, roomObjects)) {
        // Collision detected - don't move
        return;
      }

      // Apply movement (only X and Z, Y stays locked)
      this.camera.position.x = newPosition.x;
      this.camera.position.z = newPosition.z;
      this.camera.position.y = originalY; // Triple-check Y is locked
    }
  }

  /**
   * Check collision using raycasting (ONLY in current room)
   */
  checkCollision(newPosition, currentRoomObjects = null) {
    if (!this.scene) return false;

    const currentPosition = this.camera.position.clone();
    const movementVector = new THREE.Vector3().subVectors(newPosition, currentPosition);
    const movementDistance = movementVector.length();

    if (movementDistance < 0.001) return false; // No movement

    const movementDirection = movementVector.normalize();

    // Get objects to check - ONLY current room if available
    let objectsToCheck = this.scene.children;
    if (currentRoomObjects && currentRoomObjects.length > 0) {
      objectsToCheck = currentRoomObjects;
    }

    // Shoot multiple rays at different heights to detect floor objects
    const rayHeights = [
      currentPosition.y,        // Camera/eye level (1.6)
      currentPosition.y - 0.6,  // Waist level (1.0)
      currentPosition.y - 1.1   // Knee level (0.5)
    ];

    let allIntersects = [];

    for (const rayHeight of rayHeights) {
      const rayOrigin = new THREE.Vector3(currentPosition.x, rayHeight, currentPosition.z);
      const raycaster = new THREE.Raycaster(
        rayOrigin,
        movementDirection,
        0,
        movementDistance + 0.3 // Add small buffer
      );

      const intersects = raycaster.intersectObjects(objectsToCheck, true);
      allIntersects = allIntersects.concat(intersects);
    }

    // Remove duplicates (same object hit by multiple rays)
    const uniqueIntersects = [];
    const seenObjects = new Set();
    for (const intersect of allIntersects) {
      if (!seenObjects.has(intersect.object.uuid)) {
        seenObjects.add(intersect.object.uuid);
        uniqueIntersects.push(intersect);
      }
    }

    const intersects = uniqueIntersects;

    // Check if any intersections are not floor
    for (const intersect of intersects) {
      const object = intersect.object;
      const name = object.name?.toLowerCase() || '';
      const type = object.userData?.type?.toLowerCase() || '';

      // Skip floor
      if (object.userData?.isFloor ||
          name.includes('floor') ||
          name.includes('ground') ||
          type === 'floor') {
        continue;
      }

      // Skip ceiling (check if it's above camera)
      if (intersect.point.y > this.camera.position.y + 1.0) {
        continue;
      }

      // Skip artworks, paintings, sculptures
      if (object.userData?.isArtwork ||
          object.userData?.type === 'artwork' ||
          object.userData?.type === 'painting' ||
          object.userData?.type === 'sculpture' ||
          name.includes('artwork') ||
          name.includes('painting') ||
          name.includes('sculpture') ||
          name.includes('frame')) {
        continue;
      }

      // Found collision with non-floor object
      return true;
    }

    return false;
  }

  /**
   * Handle window resize
   */
  onWindowResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Reset pointer state (called on pointer leave/cancel)
   */
  resetPointerState() {
    this.isDragging = false;
    this.isZooming = false;
    this.touches.clear();
  }


  /**
   * Reset camera to a specific position (for room transitions)
   */
  resetToPosition(x, y, z) {
    // Stop any ongoing camera animation
    this.cameraAnimation = null;

    // Reset interaction state
    this.isDragging = false;
    this.isZooming = false;
    this.touches.clear();
    this.pointerDownPosition = { x: 0, y: 0 };
    this.previousPointerPosition = { x: 0, y: 0 };

    // Reset zoom (field of view) - preserve the initial FOV
    const isMobile = 'ontouchstart' in window;
    this.camera.fov = isMobile ? 75 : 50; // Match initial camera setup
    this.camera.updateProjectionMatrix();
    this.baseFov = this.camera.fov;

    // Reset camera rotation to neutral (looking forward)
    this.euler.set(0, 0, 0);
    this.camera.quaternion.setFromEuler(this.euler);
  }
}